import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import { LocationOn } from '@mui/icons-material';

const CityAutocomplete = ({ 
  value, 
  onChange, 
  onCoordinatesChange, 
  label = "Ville", 
  placeholder = "Tapez le nom d'une ville...",
  required = false,
  error = false,
  helperText = "",
  fullWidth = true 
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const debounceRef = useRef(null);

  // Fonction pour rechercher les villes via l'API Nominatim
  const searchCities = async (query) => {
    if (!query || query.length < 3) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(query)}&` +
        `format=json&` +
        `addressdetails=1&` +
        `limit=10&` +
        `countrycodes=fr&` +
        `featuretype=city`
      );
      
      if (response.ok) {
        const data = await response.json();
        
        // Filtrer et formater les résultats pour ne garder que les villes
        const cities = data
          .filter(item => 
            item.address && 
            (item.address.city || item.address.town || item.address.village) &&
            item.lat && item.lon
          )
          .map(item => ({
            id: item.place_id,
            name: item.address.city || item.address.town || item.address.village,
            displayName: item.display_name,
            latitude: parseFloat(item.lat),
            longitude: parseFloat(item.lon),
            postcode: item.address.postcode || '',
            department: item.address.state || '',
            region: item.address.region || ''
          }))
          // Supprimer les doublons basés sur le nom de la ville
          .filter((city, index, self) => 
            index === self.findIndex(c => c.name === city.name && c.postcode === city.postcode)
          );

        setOptions(cities);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche de villes:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounce pour éviter trop de requêtes API
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchCities(inputValue);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputValue]);

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const handleChange = (event, newValue) => {
    onChange(newValue ? newValue.name : '');
    
    // Si une ville est sélectionnée, mettre à jour les coordonnées
    if (newValue && onCoordinatesChange) {
      onCoordinatesChange({
        latitude: newValue.latitude,
        longitude: newValue.longitude,
        postcode: newValue.postcode
      });
    }
  };

  return (
    <Autocomplete
      fullWidth={fullWidth}
      options={options}
      getOptionLabel={(option) => option.name || ''}
      value={options.find(option => option.name === value) || null}
      onChange={handleChange}
      onInputChange={handleInputChange}
      loading={loading}
      loadingText="Recherche en cours..."
      noOptionsText="Aucune ville trouvée"
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <LocationOn sx={{ mr: 2, color: 'text.secondary' }} />
          <Box>
            <Typography variant="body1">
              {option.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {option.postcode && `${option.postcode} - `}
              {option.department}
            </Typography>
          </Box>
        </Box>
      )}
      filterOptions={(x) => x} // Désactiver le filtrage local car on utilise l'API
    />
  );
};

export default CityAutocomplete;